import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Typography,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  IconButton,
  Chip,
} from "@material-ui/core";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { Input, Select, Checkbox } from "components/Shared/mui-formik-inputs";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { AccountPermissionSchema } from "constants/validation-schemas";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { selectUser } from "redux/slices/userSlice";
import { useSelector } from "react-redux";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: "#121212",
    marginTop: "44px",
    marginBottom: "40px",
  },
  autocompleteInput: {
    "& .MuiAutocomplete-inputRoot.MuiFilledInput-root": {
      minHeight: 52,
    },
    "& .MuiChip-root": {
      height: 26,
      fontSize: 13,
    }
  },
  readonlyAutocompleteInput: {
    "& .MuiFilledInput-root.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
    },
  },
  radioGroup: {
    "& .MuiFormControlLabel-root": {
      marginLeft: 0,
      marginRight: 16,
    },
    "& .MuiRadio-root": {
      color: "#6F9CEB",
      padding: 0,
      marginRight: 8,
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiRadio-root.Mui-checked": {
      color: "#6F9CEB",
    },
  }
});

const getRouteStartButtonsFromPermissions = (permissions) => {
  const buttons = [];
  if (permissions?.create_driver || permissions?.delivery_app_doc) buttons.push("mobile");
  else if (permissions?.create_delivery ||
    permissions?.export_sorting_zipcode ||
    permissions?.export_sorting_priority ||
    permissions?.export_sorting_routing ||
    permissions?.export_sorting_alphabetical) {
    buttons.push("delivery_doc");
  }
  return buttons;
};

const getSortingTypeFromPermissions = (permissions) => {
  if (permissions?.export_sorting_zipcode) return "zipcode";
  if (permissions?.export_sorting_priority) return "priority";
  if (permissions?.export_sorting_routing) return "routing";
  if (permissions?.export_sorting_alphabetical) return "alphabetical";
  return "";
};

const getIncludeDeliveryDocFromPermissions = (permissions) => {
  return Boolean(permissions?.delivery_app_doc);
};

const toBool = (v) => v === true || v === 1 || v === "1" || v === "true";


const AccountPermissionForm = ({ initialValues, onSubmit, action }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const user = useSelector(selectUser);

  const initialRouteStartButtons = getRouteStartButtonsFromPermissions(initialValues?.permissions);
  const initialSortingType = getSortingTypeFromPermissions(initialValues?.permissions);
  const initialIncludeDeliveryDoc = getIncludeDeliveryDocFromPermissions(initialValues?.permissions);
  const initialSmtp = initialValues?.email_settings?.smtp || {};
  const passwordConfigured = Boolean(initialSmtp.passwordConfigured);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: AccountPermissionSchema,
    initialValues: {
      supplier_group_id: initialValues?.supplier_group_id ?? "",
      supplier_group_name: initialValues?.SupplierGroup?.name ?? "",
      permissions: {
        route_start_buttons: initialRouteStartButtons,
        sorting_type: initialSortingType || null,
        include_delivery_doc: initialIncludeDeliveryDoc,
        allow_atbas_order_editing: toBool(initialValues?.permissions?.allow_atbas_order_editing),
        use_tiled_global: toBool(initialValues?.permissions?.use_tiled_global),
      },
      emailSettings: {
        account_email: initialValues?.email_settings?.account_email || initialValues?.email_settings?.email || "",
        smtp: {
          email: initialSmtp.email || "",
          name: initialSmtp.name || "",
          password: "",
          host: initialSmtp.host || "",
          port: initialSmtp.port || "",
          security: initialSmtp.security || "SSL",
          passwordConfigured,
        },
        route_started: initialValues?.email_settings?.route_started || { to: [], cc: [] },
        stop_skipped: initialValues?.email_settings?.stop_skipped || { to: [], cc: [] },
        unscanned_packages: initialValues?.email_settings?.unscanned_packages || { to: [], cc: [] },
        pathway_status: initialValues?.email_settings?.pathway_status || { to: [], cc: [] },
        order_created: initialValues?.email_settings?.order_created || { to: [], cc: [] },
        return_order_unsuccessful: initialValues?.email_settings?.return_order_unsuccessful || { to: [], cc: [] },
        switch_to_manual_marking: initialValues?.email_settings?.switch_to_manual_marking || { to: [], cc: [] },
        lost_packages_report: initialValues?.email_settings?.lost_packages_report || { to: [], cc: [] },
        outside_opening_hours: initialValues?.email_settings?.outside_opening_hours || { to: [], cc: [] },
        bypassCustomerEmails: initialValues?.email_settings?.bypassCustomerEmails ?? false,
      },
      _showPassword: false,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const permissions = {};

        if (values.permissions.route_start_buttons?.includes("mobile")) {
          if (values.permissions.include_delivery_doc) {
            permissions.delivery_app_doc = true;
            permissions.create_driver = true;
          } else {
            permissions.create_driver = true;
          }
        }
        else if (values.permissions.route_start_buttons?.includes("delivery_doc")) {
          permissions.create_delivery = true;

          if (values.permissions.sorting_type) {
            permissions[`export_sorting_${values.permissions.sorting_type}`] = true;
          } else {
            permissions.export_sorting_alphabetical = true;
            setFieldValue("permissions.sorting_type", "alphabetical");
          }
        }

        if (values.permissions.allow_atbas_order_editing) {
          permissions.allow_atbas_order_editing = true;
        }

        if (values.permissions.use_tiled_global) {
          permissions.use_tiled_global = true;
        }

        const { passwordConfigured: _passwordConfigured, ...smtpRest } = values.emailSettings.smtp || {};
        const smtp = { ...smtpRest };
        if (!smtp.password || !String(smtp.password).trim()) {
          delete smtp.password;
        }

        const payload = {
          permissions,
          emailSettings: {
            ...values.emailSettings,
            smtp,
          },
        };
        await onSubmit(payload);
      } catch (err) {
        console.error("Submit error:", err);
        setSubmitting(false);
      }
    },
  });
  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    isValid,
    isSubmitting,
  } = formik;
  let { handleBlur } = formik;
  const isCustomerEmailEditable = values?.emailSettings?.bypassCustomerEmails;
  const customerEmailPlaceholder = isCustomerEmailEditable
    ? t("Add email and press Enter")
    : t("Customer Email");
  const customerEmailInputLabelProps = isCustomerEmailEditable ? {} : { shrink: true };
  const customerEmailInputClassName = isCustomerEmailEditable
    ? classes.autocompleteInput
    : `${classes.autocompleteInput} ${classes.readonlyAutocompleteInput}`;
  const routeStartedToValue = isCustomerEmailEditable
    ? values.emailSettings?.route_started?.to || []
    : [];
  const orderCreatedToValue = isCustomerEmailEditable
    ? values.emailSettings?.order_created?.to || []
    : [];
  const returnOrderUnsuccessfulToValue = isCustomerEmailEditable
    ? values.emailSettings?.return_order_unsuccessful?.to || []
    : [];
  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);
  const closeHandler = () => {
    history.push(PATHS.admin.accountPermissions);
  };

  const recipientOptions = [
    "CUSTOMER_EMAIL",
    "SUPPLIER_EMAIL",
    "SUPPLIER_LOCATION_EMAIL",
    "ACCOUNT_EMAIL",
  ];

  const recipientLabelMap = {
    CUSTOMER_EMAIL: t("Customer Email"),
    SUPPLIER_EMAIL: t("Supplier Email"),
    SUPPLIER_LOCATION_EMAIL: t("Supplier Location Email"),
    ACCOUNT_EMAIL: t("Account Email"),
    CUSTOM: t("Custom"),
    "Customer Email": t("Customer Email"),
    "Supplier Email": t("Supplier Email"),
    "Supplier Location Email": t("Supplier Location Email"),
    "Account Email": t("Account Email"),
    Custom: t("Custom"),
  };

  const renderRecipientAutocomplete = (label, name, value, onChangePath) => (
    <MuiAutocomplete
      multiple
      freeSolo
      options={recipientOptions}
      getOptionLabel={(option) => recipientLabelMap[option] || option}
      name={name}
      value={Array.isArray(value) ? value : []}
      onChange={(e, val) => setFieldValue(onChangePath, val)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            key={index}
            variant="outlined"
            label={recipientLabelMap[option] || option}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t("Select recipient type or enter email...")}
          variant="filled"
          margin="dense"
          className={classes.autocompleteInput}
        />
      )}
    />
  );
  return (
    <Box height="100%">
      {/* Page Header */}
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {t("General Settings")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>

      {/* Basic Data */}
      <Typography className={classes._subheading} variant="h5">
        {t("Basic Data")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Account")}
            name="supplier_group_name"
            value={values.supplier_group_name || user?.SupplierGroup?.name}
            disabled
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Account Email")}
            name="emailSettings.account_email"
            value={values.emailSettings?.account_email || ""}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>
      </Grid>
      <Typography className={classes._subheading} variant="h5">
        {t("Tour Setting")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography style={{ marginBottom: 8 }}>
            {t("Choose Delivery Method")}
          </Typography>
          <RadioGroup
            value={values.permissions.route_start_buttons?.[0] || ""}
            onChange={(e) => {
              setFieldValue("permissions.route_start_buttons", [e.target.value]);

              if (e.target.value !== "delivery_doc") {
                setFieldValue("permissions.sorting_type", null);
              }
              if (e.target.value !== "mobile") {
                setFieldValue("permissions.include_delivery_doc", false);
              }
            }}
            className={classes.radioGroup}
          >
            <Box display="flex" gap={2} flexWrap="wrap">
              <FormControlLabel
                value="mobile"
                control={<Radio />}
                label={t("Mobile")}
              />
              <FormControlLabel
                value="delivery_doc"
                control={<Radio />}
                label={t("Delivery Document")}
              />
              <FormControlLabel
                value=""
                control={<Radio />}
                label={t("None")}
              />
            </Box>
          </RadioGroup>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.permissions.allow_atbas_order_editing}
                onChange={(e) =>
                  setFieldValue("permissions.allow_atbas_order_editing", e.target.checked)
                }
                style={{ color: "#6F9CEB" }}
              />
            }
            label={t("allow_atbas_order_editing")}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.permissions.use_tiled_global}
                onChange={(e) =>
                  setFieldValue("permissions.use_tiled_global", e.target.checked)
                }
                style={{ color: "#6F9CEB" }}
              />
            }
            label={t("use_tiled_global")}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.permissions.use_tiled_global}
                onChange={(e) =>
                  setFieldValue("permissions.use_tiled_global", e.target.checked)
                }
                style={{ color: "#6F9CEB" }}
              />
            }
            label={t("use_tiled_global")}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.permissions.use_tiled_global}
                onChange={(e) =>
                  setFieldValue("permissions.use_tiled_global", e.target.checked)
                }
                style={{ color: "#6F9CEB" }}
              />
            }
            label={t("use_tiled_global")}
          />
        </Grid>

        {(values.permissions.route_start_buttons?.includes("delivery_doc") ||
          values.permissions.route_start_buttons?.includes("mobile")) && (
            <Grid item xs={12} sm={12} md={12} style={{ marginTop: 16 }}>
              <Box display="flex" alignItems="center">
                {values.permissions.route_start_buttons?.includes("delivery_doc") && (
                  <Box minWidth={250} sx={{ mr: 2 }}>
                    <Select
                      label={t("Document Sorting Type")}
                      name="permissions.sorting_type"
                      value={values.permissions.sorting_type || "alphabetical"}
                      onChange={(e) => {
                        setFieldValue("permissions.sorting_type", e.target.value);
                      }}
                      options={[
                        { value: "zipcode", label: t("Zipcode") },
                        { value: "priority", label: t("Priority") },
                        { value: "routing", label: t("Unsorted") },
                        { value: "alphabetical", label: t("Alphabetical") },
                      ]}
                      required
                    />
                  </Box>
                )}
                {values.permissions.route_start_buttons?.includes("mobile") && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.permissions.include_delivery_doc}
                        onChange={(e) =>
                          setFieldValue("permissions.include_delivery_doc", e.target.checked)
                        }
                        style={{ color: "#6F9CEB" }}
                      />
                    }
                    label={t("Include delivery document with efficient sorting")}
                  />
                )}
              </Box>
            </Grid>
          )}
      </Grid>
      <Typography className={classes._subheading} variant="h5">
        {t("SMTP Configuration")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Email")}
            name="emailSettings.smtp.email"
            required
            value={values?.emailSettings?.smtp?.email}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Name")}
            name="emailSettings.smtp.name"
            required
            value={values?.emailSettings?.smtp?.name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Password")}
            type={values._showPassword ? "text" : "password"}
            name="emailSettings.smtp.password"
            required={!values?.emailSettings?.smtp?.passwordConfigured}
            placeholder={
              values?.emailSettings?.smtp?.passwordConfigured
                ? t("Leave blank to keep current password")
                : undefined
            }
            value={values?.emailSettings?.smtp?.password}
            onChange={handleChange}
            onBlur={handleBlur}
            InputProps={{
              endAdornment: (

                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() =>
                      setFieldValue("_showPassword", !values._showPassword)
                    }
                  >
                    {values._showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Host")}
            name="emailSettings.smtp.host"
            required
            value={values?.emailSettings?.smtp?.host}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Port")}
            name="emailSettings.smtp.port"
            required
            value={values?.emailSettings?.smtp?.port}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Select
            label={t("Security")}
            name="emailSettings.smtp.security"
            value={values?.emailSettings?.smtp?.security}
            onChange={handleChange}
            options={[
              { value: "SSL", label: "SSL" },
              { value: "TLS", label: "TLS" },
            ]}
            required
          />
        </Grid>
      </Grid>

      <Typography className={classes._subheading} variant="h5">
        {t("Email Configuration")}
      </Typography>
      <Typography variant="h6" style={{ marginTop: 20, marginBottom: 10 }}>
        {t("Route Started")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.route_started.to",
            values.emailSettings?.route_started?.to,
            "emailSettings.route_started.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.route_started.cc",
            values.emailSettings?.route_started?.cc,
            "emailSettings.route_started.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Stop Skipped")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.stop_skipped.to",
            values.emailSettings?.stop_skipped?.to,
            "emailSettings.stop_skipped.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.stop_skipped.cc",
            values.emailSettings?.stop_skipped?.cc,
            "emailSettings.stop_skipped.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Unscanned Packages")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.unscanned_packages.to",
            values.emailSettings?.unscanned_packages?.to,
            "emailSettings.unscanned_packages.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.unscanned_packages.cc",
            values.emailSettings?.unscanned_packages?.cc,
            "emailSettings.unscanned_packages.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Pathway Status")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.pathway_status.to",
            values.emailSettings?.pathway_status?.to,
            "emailSettings.pathway_status.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.pathway_status.cc",
            values.emailSettings?.pathway_status?.cc,
            "emailSettings.pathway_status.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Order Created")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.order_created.to",
            values.emailSettings?.order_created?.to,
            "emailSettings.order_created.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.order_created.cc",
            values.emailSettings?.order_created?.cc,
            "emailSettings.order_created.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Return Order Unsuccessful")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.return_order_unsuccessful.to",
            values.emailSettings?.return_order_unsuccessful?.to,
            "emailSettings.return_order_unsuccessful.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.return_order_unsuccessful.cc",
            values.emailSettings?.return_order_unsuccessful?.cc,
            "emailSettings.return_order_unsuccessful.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Lost Packages Report")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.lost_packages_report.to",
            values.emailSettings?.lost_packages_report?.to,
            "emailSettings.lost_packages_report.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.lost_packages_report.cc",
            values.emailSettings?.lost_packages_report?.cc,
            "emailSettings.lost_packages_report.cc"
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10 }}>
        {t("Outside Opening Hours")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("To"),
            "emailSettings.outside_opening_hours.to",
            values.emailSettings?.outside_opening_hours?.to,
            "emailSettings.outside_opening_hours.to"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRecipientAutocomplete(
            t("CC"),
            "emailSettings.outside_opening_hours.cc",
            values.emailSettings?.outside_opening_hours?.cc,
            "emailSettings.outside_opening_hours.cc"
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountPermissionForm;
